'use client';
import React, { useState } from 'react';
import { AlertTriangle, Copy, MapPin, Building2, TrendingUp } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';

// Backend integration point: replace with API call to /api/taxpayer/profile/:tin
const taxpayerProfile = {
  tin: 'LR-0041820',
  name: 'Kollie & Sons Trading Company Ltd',
  type: 'Corporate Entity',
  sector: 'Import / Wholesale Trade',
  county: 'Montserrado',
  registeredDate: '03/14/2019',
  complianceScore: 72,
  complianceStatus: 'pending' as const,
  totalOwed: 4820.0,
  totalOwedLRD: 771200.0,
  lastPayment: 'Apr 18, 2026',
  lastPaymentAmount: 1250.0,
  accountManager: 'Revenue Officer: B. Kollie (MTO-01)',
  pendingActions: 2,
};

// Property summary for the header strip
const propertySummary = {
  totalProperties: 2,
  totalMarketValue: 695000,
  totalMarketValueLRD: 111200000,
  totalAnnualTax: 9795,
  totalAnnualTaxLRD: 1567200,
};

function ScoreRing({ score }: { score: number }) {
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? '#4ade80' : score >= 60 ? 'var(--accent)' : '#f87171';

  return (
    <div className="relative w-24 h-24 flex items-center justify-center flex-shrink-0">
      <svg width="96" height="96" className="-rotate-90">
        <circle cx="48" cy="48" r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
        <circle
          cx="48"
          cy="48"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="6"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-bold tabular-nums" style={{ color }}>
          {score}
        </span>
        <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>
          /100
        </span>
      </div>
    </div>
  );
}

export default function TaxpayerProfileHeader() {
  const [tinCopied, setTinCopied] = React.useState(false);

  const copyTin = () => {
    navigator.clipboard.writeText(taxpayerProfile.tin);
    setTinCopied(true);
    setTimeout(() => setTinCopied(false), 1500);
  };

  return (
    <div
      className="rounded-xl p-5 mb-4"
      style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
    >
      <div className="flex flex-col sm:flex-row items-start gap-5">
        {/* Avatar */}
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center text-xl font-bold flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)', color: 'var(--accent-foreground)' }}
        >
          K&S
        </div>

        {/* Main info */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h1 className="text-lg font-bold" style={{ color: 'var(--dark-text)' }}>
              {taxpayerProfile.name}
            </h1>
            <StatusBadge variant={taxpayerProfile.complianceStatus} />
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs mb-3" style={{ color: 'var(--dark-muted)' }}>
            <span
              className="flex items-center gap-1 font-mono-data cursor-pointer hover:opacity-80 transition-opacity"
              onClick={copyTin}
              title="Click to copy TIN"
            >
              TIN: <strong style={{ color: 'var(--dark-text)' }}>{taxpayerProfile.tin}</strong>
              <Copy size={11} className={tinCopied ? 'text-green-400' : ''} />
            </span>
            <span>{taxpayerProfile.type}</span>
            <span>{taxpayerProfile.sector}</span>
            <span className="flex items-center gap-1">
              <MapPin size={11} />
              {taxpayerProfile.county}
            </span>
            <span>Registered: {taxpayerProfile.registeredDate}</span>
          </div>

          {/* Pending actions banner */}
          {taxpayerProfile.pendingActions > 0 && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm mb-3"
              style={{
                backgroundColor: 'rgba(251,146,60,0.12)',
                border: '1px solid rgba(251,146,60,0.25)',
              }}
            >
              <AlertTriangle size={14} style={{ color: '#fb923c' }} />
              <span style={{ color: '#fb923c' }}>
                {taxpayerProfile.pendingActions} pending actions require your attention — 2 overdue filings
              </span>
            </div>
          )}

          {/* Key metrics row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total Outstanding', value: `$${taxpayerProfile.totalOwed.toLocaleString()}`, sub: `LRD ${taxpayerProfile.totalOwedLRD.toLocaleString()}`, alert: true },
              { label: 'Last Payment', value: `$${taxpayerProfile.lastPaymentAmount.toLocaleString()}`, sub: taxpayerProfile.lastPayment, alert: false },
              { label: 'Account Manager', value: 'B. Kollie', sub: taxpayerProfile.accountManager.split(':')[1]?.trim() ?? '', alert: false },
              { label: 'Filing Status', value: '2 Overdue', sub: '3 upcoming in 30 days', alert: true },
            ].map((m) => (
              <div
                key={`profile-metric-${m.label}`}
                className="rounded-lg p-2.5"
                style={{
                  backgroundColor: m.alert ? 'rgba(220,38,38,0.08)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${m.alert ? 'rgba(220,38,38,0.2)' : 'var(--dark-border)'}`,
                }}
              >
                <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                  {m.label}
                </p>
                <p className="text-sm font-semibold tabular-nums" style={{ color: m.alert ? '#fca5a5' : 'var(--dark-text)' }}>
                  {m.value}
                </p>
                <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                  {m.sub}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Compliance score ring */}
        <div className="flex flex-col items-center gap-1 flex-shrink-0">
          <ScoreRing score={taxpayerProfile.complianceScore} />
          <p className="text-xs font-semibold" style={{ color: 'var(--dark-muted)' }}>
            Compliance Score
          </p>
          <p className="text-xs" style={{ color: '#fb923c' }}>
            Needs improvement
          </p>
        </div>
      </div>

      {/* ── Property Portfolio Summary Strip ── */}
      <div
        className="mt-4 pt-4 grid grid-cols-2 sm:grid-cols-4 gap-3"
        style={{ borderTop: '1px solid var(--dark-border)' }}
      >
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(212,160,23,0.12)' }}
          >
            <Building2 size={14} style={{ color: 'var(--accent)' }} />
          </div>
          <div>
            <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Registered Properties</p>
            <p className="text-sm font-extrabold" style={{ color: 'var(--dark-text)' }}>
              {propertySummary.totalProperties} Properties
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(212,160,23,0.12)' }}
          >
            <TrendingUp size={14} style={{ color: 'var(--accent)' }} />
          </div>
          <div>
            <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Total Market Value</p>
            <p className="text-sm font-extrabold tabular-nums" style={{ color: 'var(--accent)' }}>
              ${propertySummary.totalMarketValue.toLocaleString()}
            </p>
            <p className="text-xs font-mono-data" style={{ color: 'var(--dark-muted)' }}>
              LRD {propertySummary.totalMarketValueLRD.toLocaleString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(248,113,113,0.1)' }}
          >
            <TrendingUp size={14} style={{ color: '#f87171' }} />
          </div>
          <div>
            <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Annual Property Tax</p>
            <p className="text-sm font-extrabold tabular-nums" style={{ color: '#f87171' }}>
              ${propertySummary.totalAnnualTax.toLocaleString()}
            </p>
            <p className="text-xs font-mono-data" style={{ color: 'var(--dark-muted)' }}>
              LRD {propertySummary.totalAnnualTaxLRD.toLocaleString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(74,222,128,0.1)' }}
          >
            <Building2 size={14} style={{ color: '#4ade80' }} />
          </div>
          <div>
            <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>LRA Registry Status</p>
            <p className="text-sm font-extrabold" style={{ color: '#4ade80' }}>
              All Registered
            </p>
            <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
              Titles Verified
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}